import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowsClockwiseIcon,
  ShieldSlashIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { Spinner } from '@voll-intelligence/ui-kit/ui/spinner';
import { AirlineLogo } from '../../shared/airline_logo/airline_logo.js';
import { fmtMoneyWhole, fmtWeekdayDayMonth } from '../results/flight_format.js';
import { KIND_GLYPH, flightNumberLabel } from '../results/offer/flight_leg.js';
import { StackLeg } from '../results/offer/stack_leg.js';
import { FareRail } from './fare_rail.js';
import { hasReturnLadder } from './fare_selection.js';
import type { FlightFareSource } from './fare_source.js';
import type { FaresText, FlightFaresCopy } from './fares_copy.js';
import type { FaresDrawingProps } from './fares_page.js';
import {
  FaresStatus,
  type FaresLegKind,
  type FaresPageFlight,
  type OpenFlightDetail,
} from './fares_page_shared.js';

/**
 * **"Escolha as tarifas" no CELULAR** (#3875) — o frame "Seleção de tarifa"
 * do Figma, em utilitárias do DS. A `FlightFaresPage` despacha para cá quando o
 * container mede menos que `sm`; a derivação (tariff, combos, resumo, aviso de
 * bagagem assimétrica) já vem pronta de lá, e os gestos são os mesmos.
 *
 * Um bloco por perna: cabeçalho `Ida · Sex, 25 set` com "Alterar voo" à
 * direita, o voo escolhido num cartão com a anatomia do card de oferta do
 * celular (`StackLeg`), e o trilho de famílias com pontos. A saia larga do
 * desktop vira uma faixa fixa no pé com o único CTA — o resumo por perna que a
 * saia escrevia não cabe, e o que a pessoa escolheu está a um rolar de
 * distância, marcado no card. Bundled (a companhia tarifa o par junto) mantém
 * os dois cartões e um trilho só, embaixo dos dois, pela mesma razão do
 * desenho largo.
 */
export function FaresPageStack({
  subtitle,
  cabin,
  flights,
  state,
  sel,
  readOnly,
  ceiling,
  bundled,
  combos,
  summary,
  asymmetric,
  onOpenDetail,
  onPick,
  onChangeFlight,
  onCheckout,
  onRetry,
  onResearch,
  onBack,
  ctaLabel,
  ctaDisabled,
  ctaBusy,
  notice,
  text,
}: Readonly<FaresDrawingProps>) {
  const { copy } = text;
  const view = state.phase === 'ready' ? state.view : null;
  // O trilho sangra o padding da tela (`-mx-4`): o card seguinte aparece
  // cortado na BORDA do celular, como no frame, e não 16px antes dela.
  const railOf = (
    fares: readonly FlightFareSource[],
    kind: FaresLegKind,
    selected: string | undefined,
  ) => (
    <div className="-mx-4 w-auto min-w-0">
      <FareRail
        label={copy.faresOf(legWord(kind, flights.length, copy))}
        fares={fares}
        selected={selected}
        nav="dots"
        {...(readOnly ? { readOnly } : {})}
        onPick={(fareId) => {
          onPick(kind === 'return' ? { return: fareId } : { outbound: fareId });
        }}
        text={text}
      />
    </div>
  );
  // O trilho de UMA perna (split), ou nada: bundled e os estados sem tariff
  // desenham o que vai embaixo dos cartões fora do bloco da perna.
  const railFor = (kind: FaresLegKind) => {
    if (!view || bundled) return null;
    if (kind === 'return') {
      return hasReturnLadder(view) ? railOf(view.returning, 'return', sel.return) : null;
    }
    return railOf(view.departing, 'outbound', sel.outbound);
  };
  return (
    <>
      <StackHead subtitle={subtitle} onBack={onBack} copy={copy} />
      {notice}
      {ceiling && (
        <p
          className="m-0 flex items-center gap-1.5 text-caption font-medium text-muted-foreground"
          role="note"
        >
          <ShieldSlashIcon size={14} className="shrink-0 text-destructive" aria-hidden />
          <span className="truncate">
            {copy.policyCeilingAbove(fmtMoneyWhole(ceiling))}{' '}
            <span className="underline">{copy.policyCeilingOut}</span>
          </span>
        </p>
      )}
      {flights.map((flight, i) => {
        const kind: FaresLegKind = i === 0 ? 'outbound' : 'return';
        return (
          <LegBlock
            key={kind}
            kind={kind}
            legCount={flights.length}
            flight={flight}
            cabin={cabin}
            readOnly={readOnly}
            onOpenDetail={onOpenDetail}
            text={text}
            onChangeFlight={() => {
              onChangeFlight(i === 0 ? 0 : 1);
            }}
          >
            {railFor(kind)}
          </LegBlock>
        );
      })}
      {view && bundled && (
        <div className="-mx-4 w-auto min-w-0">
          <FareRail
            label={copy.faresOf(copy.bothLegs)}
            fares={combos}
            selected={sel.bundled}
            nav="dots"
            {...(readOnly ? { readOnly } : {})}
            onPick={(fareId) => {
              onPick({ bundled: fareId });
            }}
            text={text}
          />
        </div>
      )}
      {!view && (
        <FaresStatus
          state={state}
          onRetry={onRetry}
          onResearch={onResearch}
          onChangeFlight={onChangeFlight}
          readOnly={readOnly}
          copy={copy}
        />
      )}
      {asymmetric && (
        <p className="ffs-warn" role="note">
          <WarningIcon size={14} weight="fill" aria-hidden />
          <span>{copy.asymmetric(asymmetric.kind, asymmetric.leg)}</span>
        </p>
      )}
      {/* Congelada pelo host que está PROCESSANDO o pick (`ctaBusy`), a faixa
          fica — travada e girando: no celular esta tela cobre qualquer faixa
          de aviso do host, então a resposta ao clique tem que estar aqui. */}
      {(!readOnly || ctaBusy) && summary && (
        // `sticky` e não `fixed`, pela razão da saia larga: o takeover do chat
        // anima com `transform`, e `fixed` sob ele sairia do lugar. O `-mb-5`
        // come o respiro de 20px do `.ffs` para a faixa encostar no pé; o
        // `pb` soma o safe-area do iPhone.
        <div className="sticky bottom-0 z-1 -mx-4 -mb-5 mt-auto w-auto rounded-t-xl border-t border-border bg-background px-4 pt-3 pb-[calc(24px+env(safe-area-inset-bottom,0px))]">
          <Button
            size="xl"
            className="w-full rounded-full"
            disabled={ctaDisabled || ctaBusy || !summary.complete}
            aria-busy={ctaBusy || undefined}
            onClick={() => {
              onCheckout({
                total: summary.total,
                currency: summary.currency,
                names: summary.names,
              });
            }}
          >
            {ctaLabel}
            {ctaBusy ? <Spinner aria-hidden /> : <ArrowRightIcon aria-hidden />}
          </Button>
        </div>
      )}
    </>
  );
}

/** "Ida"/"Volta" no ida e volta; "Voo" quando a perna é única (o `legSingle`
 *  do card do chat — só um binário tem ida). */
function legWord(kind: FaresLegKind, legCount: number, copy: FlightFaresCopy): string {
  return legCount > 1 ? copy.offer.leg.kind[kind] : copy.offer.legSingle;
}

/** Voltar (quando a casca a passa) + título e subtítulo, encurtando com
 *  reticências. O h1 é o mesmo do desenho largo: é a página que a pessoa está
 *  lendo, e o foco pousa nele ao voltar do checkout (`useResumeSeq`). */
function StackHead({
  subtitle,
  onBack,
  copy,
}: Readonly<{ subtitle: string; onBack: (() => void) | undefined; copy: FlightFaresCopy }>) {
  return (
    <header className="flex items-center gap-3">
      {onBack && (
        <Button
          variant="ghost"
          size="icon"
          className="-ml-1 shrink-0"
          aria-label={copy.back}
          onClick={onBack}
        >
          <ArrowLeftIcon className="size-6" aria-hidden />
        </Button>
      )}
      <div className="grid min-w-0">
        <h1 className="m-0 truncate text-heading text-foreground" tabIndex={-1}>
          {copy.titleShort}
        </h1>
        <p className="m-0 truncate text-detail font-medium text-muted-foreground">{subtitle}</p>
      </div>
    </header>
  );
}

/** Uma perna: cabeçalho com o sentido e a data, "Alterar voo", o cartão do voo
 *  escolhido e, como filho, o trilho dela. */
function LegBlock({
  kind,
  legCount,
  flight,
  cabin,
  readOnly,
  onOpenDetail,
  onChangeFlight,
  text,
  children,
}: Readonly<{
  kind: FaresLegKind;
  legCount: number;
  flight: FaresPageFlight;
  cabin: string | undefined;
  readOnly: boolean | undefined;
  onOpenDetail: OpenFlightDetail;
  onChangeFlight: () => void;
  text: FaresText;
  children: React.ReactNode;
}>) {
  const { copy, locale } = text;
  const Glyph = KIND_GLYPH[kind];
  const word = legWord(kind, legCount, copy);
  const seg = flight.seg;
  return (
    <section className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-0.5" aria-label={word}>
      <div className="flex items-center justify-between pt-1 pl-2">
        <span className="flex items-center gap-1.5 text-ui text-foreground">
          <Glyph size={16} aria-hidden />
          {word} · {fmtWeekdayDayMonth(seg.departAt, locale)}
        </span>
        {!readOnly && (
          <Button
            variant="ghost"
            size="lg"
            className="-mr-2.5 font-semibold"
            aria-label={copy.changeFlightAria(word.toLowerCase())}
            onClick={onChangeFlight}
          >
            <ArrowsClockwiseIcon aria-hidden />
            {copy.changeFlight}
          </Button>
        )}
      </div>
      <div className="rounded-xl border border-border bg-card px-4 pt-3.5 pb-4 text-ui text-foreground">
        <div className="flex items-center justify-between gap-2 text-ui font-normal text-muted-foreground">
          <span className="flex min-w-0 items-center gap-1">
            <AirlineLogo
              {...(seg.carrier ? { iata: seg.carrier } : {})}
              {...(seg.carrierName ? { name: seg.carrierName } : {})}
              size={24}
            />
            <span className="truncate">{flightNumberLabel(seg.carrier, seg.flightNumber)}</span>
          </span>
          {cabin && <span className="shrink-0">{cabin}</span>}
        </div>
        <StackLeg
          seg={seg}
          stops={flight.stops}
          head="none"
          text={{ copy: copy.offer, locale, places: text.places }}
          onDetail={() => {
            onOpenDetail(flight, kind);
          }}
          // Trecho DIRETO só: o mapa é pedido por identidade de perna (mesma
          // regra da linha de ofertas e do desenho largo).
          {...(flight.stops <= 0
            ? {
                onSeats: () => {
                  onOpenDetail(flight, kind, 'seats');
                },
              }
            : {})}
        />
      </div>
      {children}
    </section>
  );
}
